import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Flex, Form, Input } from 'antd';
import dayjs from 'dayjs';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import { getRequiredPersonalFields } from '../../hooks/verification/verificationRules';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import AddressFields from '../form/address/AddressFields';
import PhoneNumberInput from '../form/general/PhoneNumberInput';
import useHelpers from '../../hooks/context/useHelpers';

export default function PersonalInfoStep(props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:PersonalInfoStep' });
  const { buttonText, moveBackwards, moveForward } = props;

  const { getValue } = useBusinessProfile();
  const { dateInputFormat } = useHelpers();
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });
  const requiredPersonalFields = getRequiredPersonalFields(getValue('legal_country'));
  const { updating, pushUpdates } = useBusinessProfile();

  const { data, update } = useForm({
    owner_phone_number: getValue('owner_phone_number'),
    owner_email: getValue('owner_email'),
    owner_first_name: getValue('owner_first_name'),
    owner_last_name: getValue('owner_last_name'),
    owner_dob: getValue('owner_dob'),
    owner_id_number: getValue('owner_id_number'),
    owner_address: getValue('owner_address'),
    owner_city: getValue('owner_city'),
    owner_state: getValue('owner_state'),
    owner_country: getValue('owner_country'),
    owner_zip_code: getValue('owner_zip_code'),
  });

  const save = () =>
    pushUpdates({
      data,
      onSuccess: () => moveForward && moveForward(),
      onError: ({ data }) => setErrorFeedback(data),
    });

  return (
    <FadeIn>
      {renderFeedbackLabel}
      <Form layout="vertical">
        <Form.Item label={t('form.owner_phone_number.label')} required>
          <PhoneNumberInput
            defaultCountry={getValue('legal_country')}
            value={data.owner_phone_number ?? ''}
            onChange={(value) => update('owner_phone_number', value)}
            international
            className="w-full font-sm relative"
            countryCallingCodeEditable={false}
          />
        </Form.Item>
        <Form.Item label={t('form.owner_email.label')} required>
          <Input
            value={data.owner_email}
            autocomplete={null}
            onChange={(e) => update('owner_email', e.target.value)}
            placeholder={t('form.owner_email.placeholder')}
            type="email"
          />
        </Form.Item>
        <Form.Item label={t('form.owner_first_name.label')} required>
          <Input
            placeholder={t('form.owner_first_name.placeholder')}
            value={data.owner_first_name ?? ''}
            onChange={(e) => update('owner_first_name', e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('form.owner_last_name.label')} required>
          <Input
            placeholder={t('form.owner_last_name.placeholder')}
            value={data.owner_last_name ?? ''}
            onChange={(e) => update('owner_last_name', e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('form.owner_dob.label')} required>
          <DatePicker
            format={[dateInputFormat, 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY']}
            value={data.owner_dob ? dayjs(data.owner_dob) : null}
            placeholder={t('form.owner_dob.placeholder')}
            onChange={(value) => update('owner_dob', value.utc(true).toISOString())}
          />
        </Form.Item>
        {requiredPersonalFields.includes('owner_id_number') && (
          <Form.Item label={t('form.owner_id_number.label')} required>
            <Input
              value={data.owner_id_number}
              placeholder={t('form.owner_id_number.placeholder')}
              onChange={(e) => update('owner_id_number', e.target.value)}
            />
          </Form.Item>
        )}
        <AddressFields prefix="owner" useGrid data={data} onChange={update} />
      </Form>
      <Flex justify="end">
        {moveBackwards && (
          <Button onClick={moveBackwards} className="mr-md">
            {t('buttons.back')}
          </Button>
        )}
        <Button type="primary" onClick={save} loading={updating}>
          {buttonText ?? t('buttons.next')}
        </Button>
      </Flex>
    </FadeIn>
  );
}
